/** Parent-frame checks use origins. Custom finder domains are HTTPS hostnames. */

export interface EmbedWindowLike {
  self: unknown;
  top: unknown | null;
  location: {
    origin: string;
    ancestorOrigins?: ArrayLike<string> & { item?: (index: number) => string | null };
  };
  document: { referrer: string };
}

export interface EmbedParentContext {
  isFramed: boolean;
  /** Immediate parent first, then outer ancestors when `ancestorOrigins` is available. */
  parentOrigins: string[];
  selfOrigin: string;
  appOrigin: string | null;
}

export function parseEmbedAllowedOrigin(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed || trimmed.startsWith('//')) {
    return null;
  }

  const candidate = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return null;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return null;
  }
  if (!url.hostname) {
    return null;
  }

  return url.origin;
}

export function parseAppOrigin(appUrl: string): string | null {
  try {
    return new URL(appUrl).origin;
  } catch {
    return null;
  }
}

export function sanitizeEmbedAllowedOrigins(raw: readonly string[]): string[] {
  const seen = new Set<string>();
  const origins: string[] = [];
  for (const value of raw) {
    const origin = parseEmbedAllowedOrigin(value);
    if (!origin || seen.has(origin)) {
      continue;
    }
    seen.add(origin);
    origins.push(origin);
  }
  return origins;
}

/** Origins that may iframe a finder: a verified custom hostname and/or extra parent sites. */
export function embedOriginsForCustomHostname(
  hostname: string | null | undefined,
): string[] {
  if (!hostname?.trim()) {
    return [];
  }
  const origin = parseEmbedAllowedOrigin(hostname);
  return origin ? [origin] : [];
}

export function collectAllowedEmbedOrigins(
  verifiedCustomHostname: string | null | undefined,
  extraOrigins: readonly string[] | null | undefined,
): string[] {
  return sanitizeEmbedAllowedOrigins([
    ...embedOriginsForCustomHostname(verifiedCustomHostname),
    ...(extraOrigins ?? []),
  ]);
}

export function readEmbedParentContext(win: EmbedWindowLike, appUrl: string): EmbedParentContext {
  const isFramed = win.self !== win.top;
  return {
    isFramed,
    parentOrigins: isFramed ? collectParentOrigins(win) : [],
    selfOrigin: win.location.origin,
    appOrigin: parseAppOrigin(appUrl),
  };
}

/**
 * Direct (non-iframe) visits are always allowed.
 * Iframes are allowed only on `allowedParentOrigins` plus this app (live preview).
 */
export function isFinderEmbedAllowed(
  allowedParentOrigins: readonly string[],
  ctx: EmbedParentContext,
): boolean {
  if (!ctx.isFramed) {
    return true;
  }

  const allowed = new Set<string>(sanitizeEmbedAllowedOrigins(allowedParentOrigins));
  if (ctx.appOrigin) {
    allowed.add(ctx.appOrigin);
  }

  if (ctx.parentOrigins.length === 0) {
    return false;
  }

  return ctx.parentOrigins.every((origin) => allowed.has(origin));
}

function collectParentOrigins(win: EmbedWindowLike): string[] {
  const fromAncestors = readAncestorOrigins(win);
  if (fromAncestors.length > 0) {
    return fromAncestors;
  }

  const referrer = win.document.referrer?.trim();
  if (!referrer) {
    return [];
  }
  try {
    return [new URL(referrer).origin];
  } catch {
    return [];
  }
}

function readAncestorOrigins(win: EmbedWindowLike): string[] {
  const list = win.location.ancestorOrigins;
  if (!list?.length) {
    return [];
  }

  const origins: string[] = [];
  for (let i = 0; i < list.length; i++) {
    const value = list.item?.(i) ?? list[i];
    if (value) {
      origins.push(value);
    }
  }
  return origins;
}
